import { useEffect, useRef, useState, useCallback, useMemo, useLayoutEffect, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Search, Building2, Pencil, Trash2, SlidersHorizontal, X } from 'lucide-react';
import { Toast } from '../../components/ui/FormField';
import { CompanyForm } from '../../components/forms/CompanyForm';
import { StatsCard } from '../../components/ui/StatsCard';
import { SortHeader, makeSortToggle } from '../../components/ui/SortHeader';
import { useCrudPage } from '../../hooks/useCrudPage';
import { usePageAction } from '../../stores/page-store';
import { useActivityStore } from '../../stores/activity-store';
import { useAuthStore } from '../../stores/auth-store';
import { companyApi } from '../../services/api-services';
import type { Company, ListQuery } from '../../types/api';

// ─────────────────────────────────────────────────────────────
// Companies Management — Operations Deck (Variation A)
// Form lives in components/forms/CompanyForm.tsx
// ─────────────────────────────────────────────────────────────

// Shared column template — keeps header and rows aligned.
// Columns: Company | First Name | Last Name | Email | Reg # | Telephone | Location | Actions
const COL_TEMPLATE = 'minmax(220px,1.6fr) minmax(110px,0.9fr) minmax(110px,0.9fr) minmax(200px,1.5fr) minmax(110px,0.9fr) minmax(120px,1fr) minmax(160px,1.1fr) 88px';

// State / Province → 2-letter ISO abbreviation lookup
const STATE_ABBREV: Record<string, string> = {
  // US States
  Alabama:'AL', Alaska:'AK', Arizona:'AZ', Arkansas:'AR', California:'CA',
  Colorado:'CO', Connecticut:'CT', Delaware:'DE', Florida:'FL', Georgia:'GA',
  Hawaii:'HI', Idaho:'ID', Illinois:'IL', Indiana:'IN', Iowa:'IA',
  Kansas:'KS', Kentucky:'KY', Louisiana:'LA', Maine:'ME', Maryland:'MD',
  Massachusetts:'MA', Michigan:'MI', Minnesota:'MN', Mississippi:'MS', Missouri:'MO',
  Montana:'MT', Nebraska:'NE', Nevada:'NV', 'New Hampshire':'NH', 'New Jersey':'NJ',
  'New Mexico':'NM', 'New York':'NY', 'North Carolina':'NC', 'North Dakota':'ND',
  Ohio:'OH', Oklahoma:'OK', Oregon:'OR', Pennsylvania:'PA', 'Rhode Island':'RI',
  'South Carolina':'SC', 'South Dakota':'SD', Tennessee:'TN', Texas:'TX', Utah:'UT',
  Vermont:'VT', Virginia:'VA', Washington:'WA', 'West Virginia':'WV',
  Wisconsin:'WI', Wyoming:'WY', 'District of Columbia':'DC',
  // Canadian Provinces / Territories
  Alberta:'AB', 'British Columbia':'BC', Manitoba:'MB', 'New Brunswick':'NB',
  'Newfoundland and Labrador':'NL', 'Northwest Territories':'NT', 'Nova Scotia':'NS',
  Nunavut:'NU', Ontario:'ON', 'Prince Edward Island':'PE', Quebec:'QC',
  Saskatchewan:'SK', Yukon:'YT',
};

function abbrevState(s: string): string {
  if (!s) return '';
  const trimmed = s.trim();
  if (trimmed.length <= 2) return trimmed.toUpperCase();
  return STATE_ABBREV[trimmed] ?? trimmed.slice(0, 2).toUpperCase();
}

// Country shorthand — keeps the location column compact.
// We strip EVERY non-alphanumeric character before lookup so any spelling drift
// (case, punctuation, whitespace, NBSP, "U.S.A.", "united states of america", etc.)
// reliably collapses to the same key.
const COUNTRY_SHORT: Record<string, string> = {
  unitedstatesofamerica: 'USA',
  unitedstates: 'USA',
  usa: 'USA',
  us: 'USA',
  america: 'USA',
  unitedkingdom: 'UK',
  uk: 'UK',
  greatbritain: 'UK',
  england: 'UK',
};
function shortCountry(c: string): string {
  const key = c.toLowerCase().replace(/[^a-z0-9]/g, '');
  return COUNTRY_SHORT[key] ?? c.trim();
}

function formatLocation(city?: string, state?: string, country?: string): string {
  const parts: string[] = [];
  if (city?.trim()) parts.push(city.trim());
  if (state?.trim()) parts.push(abbrevState(state.trim()));
  const cityState = parts.join(', ');
  const cty = country?.trim() ? shortCountry(country) : '';
  if (cityState && cty) return `${cityState} · ${cty}`;
  return cityState || cty || '—';
}

type StatusFilter = 'active' | 'inactive' | 'all';

export function CompaniesPage() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [countryFilter, setCountryFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');

  // Server-side: getAllActive when filter == active; else fetch all and filter
  // the remaining states client-side. (Backend doesn't yet expose country/state/city filters.)
  const fetchFn = useCallback(
    (q: ListQuery) => (statusFilter === 'active' ? companyApi.getAllActive(q) : companyApi.getAll(q)),
    [statusFilter],
  );

  const crud = useCrudPage<Company>({ fetchFn });
  const [saving, setSaving] = useState(false);

  const recordActivity = useActivityStore((s) => s.record);
  const username = useAuthStore((s) => s.user?.username);

  const handleSave = async (form: Partial<Company>) => {
    setSaving(true);
    try {
      const label = form.companyName?.trim() || 'Untitled company';
      if (crud.editingItem) {
        await companyApi.update(form);
        crud.showToast('Company updated successfully');
        recordActivity({ type: 'update', entity: 'Companies', label, user: username });
      } else {
        await companyApi.create(form);
        crud.showToast('Company created successfully');
        recordActivity({ type: 'create', entity: 'Companies', label, user: username });
      }
      crud.closeModal();
      crud.load();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save company';
      crud.showToast(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number, name?: string) => {
    if (!confirm('Are you sure you want to delete this company?')) return;
    try {
      await companyApi.delete(id);
      crud.showToast('Company deleted');
      recordActivity({
        type: 'delete',
        entity: 'Companies',
        label: name?.trim() || `Company #${id}`,
        user: username,
      });
      crud.load();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete company';
      crud.showToast(message, 'error');
    }
  };

  // Client-side filter pass on the current page (country/state/city/status).
  // Backend doesn't expose these yet — see DEV-NOTES for pending API work.
  const filteredData = useMemo(() => {
    return crud.data.filter((c) => {
      if (statusFilter === 'inactive' && !c.inactive) return false;
      if (statusFilter === 'active' && c.inactive) return false;
      if (countryFilter && (c.country ?? '').trim() !== countryFilter) return false;
      if (stateFilter && (c.stateProvince ?? '').trim() !== stateFilter) return false;
      if (cityFilter && !(c.city ?? '').toLowerCase().includes(cityFilter.toLowerCase())) return false;
      return true;
    });
  }, [crud.data, statusFilter, countryFilter, stateFilter, cityFilter]);

  // Distinct values from the loaded page — powers Country/State dropdowns.
  const availableCountries = useMemo(() => {
    const s = new Set<string>();
    crud.data.forEach((c) => c.country?.trim() && s.add(c.country.trim()));
    return Array.from(s).sort();
  }, [crud.data]);
  const availableStates = useMemo(() => {
    const s = new Set<string>();
    crud.data.forEach((c) => {
      if (countryFilter && (c.country ?? '').trim() !== countryFilter) return;
      if (c.stateProvince?.trim()) s.add(c.stateProvince.trim());
    });
    return Array.from(s).sort();
  }, [crud.data, countryFilter]);

  const filtersActive = !!countryFilter || !!stateFilter || !!cityFilter || statusFilter !== 'active';
  const clearFilters = () => {
    setCountryFilter('');
    setStateFilter('');
    setCityFilter('');
    setStatusFilter('active');
  };

  // Stats — computed from the current (filtered) page.
  const stats = useMemo(() => {
    const active = crud.data.filter((c) => !c.inactive).length;
    const inactiveCnt = crud.data.filter((c) => c.inactive).length;
    const withEmail = crud.data.filter((c) => !!c.contactPersonEmail?.trim()).length;
    return { total: crud.totalCount, active, inactive: inactiveCnt, withEmail };
  }, [crud.data, crud.totalCount]);

  const totalPages = Math.max(1, Math.ceil(crud.totalCount / crud.pageSize));
  const currentPage = Math.floor(crud.offset / crud.pageSize) + 1;

  const handleSort = makeSortToggle(crud.sortCol, crud.sortDir, crud.setSortCol, crud.setSortDir);

  // Publish the page's primary action into the TopBar slot.
  usePageAction(
    <PrimaryButton onClick={crud.openCreate}>
      <Plus size={14} /> Register company
    </PrimaryButton>,
    [],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      {/* Stats strip */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <StatsCard label="Registered" value={stats.total} sub="Companies in portal" accent="var(--color-accent)" loading={crud.loading} />
        <StatsCard label="Active (this page)" value={stats.active} sub={statusFilter === 'active' ? 'matching active filter' : 'of the current page'} accent="var(--color-teal)" loading={crud.loading} />
        <StatsCard label="Inactive (this page)" value={stats.inactive} sub={statusFilter === 'active' ? 'hidden by filter' : 'on this page'} accent="var(--color-warn)" loading={crud.loading} />
        <StatsCard label="With email" value={stats.withEmail} sub="Contactable primary contacts" accent="#7c6ba8" loading={crud.loading} />
      </div>

      {/* Filter / search bar */}
      <div
        style={{
          background: 'var(--color-panel)',
          border: '1px solid var(--color-border)',
          borderRadius: 10,
          overflow: 'hidden',
        }}
      >
        {/* Single filter row — Search + Status + Filter popover + count */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '14px 18px',
            borderBottom: '1px solid var(--color-border)',
            flexWrap: 'wrap',
          }}
        >
          {/* Search */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '7px 12px',
              background: 'var(--color-panel-alt)',
              border: '1px solid var(--color-border)',
              borderRadius: 8,
              width: 280,
              transition: 'border-color 180ms',
            }}
            className="focus-within:!border-[var(--color-accent-bright)]"
          >
            <Search size={15} style={{ color: 'var(--color-text-dim)' }} />
            <input
              placeholder="Search companies…"
              value={crud.searchText}
              onChange={(e) => {
                crud.setSearchText(e.target.value);
                crud.setOffset(0);
              }}
              style={{
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: 13.5,
                flex: 1,
                fontFamily: 'var(--font-body)',
                color: 'var(--color-text)',
                width: '100%',
              }}
            />
            {crud.searchText && (
              <button
                onClick={() => { crud.setSearchText(''); crud.setOffset(0); }}
                aria-label="Clear search"
                title="Clear search"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 18, height: 18, borderRadius: 9, padding: 0,
                  background: 'transparent', border: 'none',
                  color: 'var(--color-text-dim)', cursor: 'pointer', flexShrink: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(15,23,32,0.08)';
                  e.currentTarget.style.color = 'var(--color-text)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = 'var(--color-text-dim)';
                }}
              >
                <X size={13} />
              </button>
            )}
          </div>

          {/* Status — 3-way segmented control */}
          <StatusSegmented
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter(v);
              crud.setOffset(0);
            }}
          />

          {/* Filter popover — Country / State / City */}
          <FilterPopover
            countryFilter={countryFilter}
            stateFilter={stateFilter}
            cityFilter={cityFilter}
            availableCountries={availableCountries}
            availableStates={availableStates}
            onChangeCountry={(v) => {
              setCountryFilter(v);
              if (v && stateFilter && availableStates.indexOf(stateFilter) === -1) setStateFilter('');
            }}
            onChangeState={setStateFilter}
            onChangeCity={setCityFilter}
            onClear={clearFilters}
            activeCount={(countryFilter ? 1 : 0) + (stateFilter ? 1 : 0) + (cityFilter ? 1 : 0)}
          />

          {filtersActive && (
            <button
              onClick={clearFilters}
              style={{
                fontSize: 12,
                color: 'var(--color-accent)',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '6px 8px',
                borderRadius: 5,
                fontFamily: 'var(--font-body)',
                fontWeight: 500,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(11,74,111,0.08)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              Clear all
            </button>
          )}

          <div style={{ flex: 1 }} />

          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--color-text-dim)' }}>
            {crud.loading
              ? 'Loading…'
              : filtersActive
                ? `${filteredData.length} of ${crud.data.length} on page · ${crud.totalCount} total`
                : `${crud.totalCount} record${crud.totalCount === 1 ? '' : 's'}`}
          </div>
        </div>

        {/* Header row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: COL_TEMPLATE,
            padding: '12px 18px',
            borderBottom: '1px solid var(--color-border)',
            background: 'var(--color-panel-alt)',
            fontSize: 12.5,
            letterSpacing: 0.3,
            color: 'var(--color-text-mid)',
            fontWeight: 600,
            fontFamily: 'var(--font-body)',
          }}
        >
          <SortHeader label="Company" col="CompanyName" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
          <SortHeader label="First Name" col="ContactPersonFirstName" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
          <SortHeader label="Last Name" col="ContactPersonLastName" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
          <SortHeader label="Email" col="ContactPersonEmail" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
          <SortHeader label="Reg #" col="RegistrationNumber" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
          <SortHeader label="Telephone" col="Tel" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
          <SortHeader label="Location" col="City" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
          <div style={{ textAlign: 'right' }}>Actions</div>
        </div>

        {/* Rows */}
        {crud.loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--color-text-dim)', fontSize: 13 }}>
            Loading…
          </div>
        ) : filteredData.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--color-text-dim)' }}>
            <Building2 size={32} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
            <div style={{ fontSize: 14, marginBottom: 4 }}>
              {filtersActive ? 'No companies match these filters' : 'No companies found'}
            </div>
            <div style={{ fontSize: 12 }}>
              {filtersActive
                ? 'Try clearing filters or moving to another page.'
                : 'Register a new company to get started.'}
            </div>
          </div>
        ) : (
          <div>
            {filteredData.map((c, i) => (
              <CompanyRow
                key={c.id ?? i}
                company={c}
                isLast={i === filteredData.length - 1}
                onEdit={() => crud.openEdit(c)}
                onDelete={() => c.id && handleDelete(c.id, c.companyName)}
              />
            ))}
          </div>
        )}

        {/* Pagination footer */}
        <div
          style={{
            padding: '12px 18px',
            borderTop: '1px solid var(--color-border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 12,
            color: 'var(--color-text-dim)',
            background: 'var(--color-panel-alt)',
          }}
        >
          <div>
            Showing{' '}
            <span style={{ color: 'var(--color-text-mid)', fontWeight: 500 }}>{filteredData.length}</span>
            {filtersActive && filteredData.length !== crud.data.length && (
              <> (of {crud.data.length} on page)</>
            )}{' '}
            of <span style={{ color: 'var(--color-text-mid)', fontWeight: 500 }}>{crud.totalCount}</span> companies
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-mono)' }}>
              <PaginationButton onClick={() => crud.setOffset(Math.max(0, crud.offset - crud.pageSize))} disabled={crud.offset === 0}>
                ←
              </PaginationButton>
              <span style={{ padding: '0 10px', color: 'var(--color-text-mid)' }}>
                {currentPage} / {totalPages}
              </span>
              <PaginationButton onClick={() => crud.setOffset(crud.offset + crud.pageSize)} disabled={currentPage >= totalPages}>
                →
              </PaginationButton>
            </div>
          )}
        </div>
      </div>

      {/* Operations Deck form */}
      <CompanyForm
        open={crud.modalOpen}
        editing={crud.editingItem}
        saving={saving}
        onCancel={crud.closeModal}
        onSave={handleSave}
      />

      {crud.toast && <Toast message={crud.toast.message} type={crud.toast.type} onClose={() => crud.setToast(null)} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Sub-components (kept in-file — extract later if reused)
// ─────────────────────────────────────────────────────────────

function CompanyRow({
  company: c,
  isLast,
  onEdit,
  onDelete,
}: {
  company: Company;
  isLast: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [hover, setHover] = useState(false);
  const statusColor = c.inactive ? 'var(--color-text-dim)' : 'var(--color-good)';

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'grid',
        gridTemplateColumns: COL_TEMPLATE,
        padding: '14px 18px',
        borderBottom: isLast ? 'none' : '1px solid var(--color-border)',
        alignItems: 'center',
        fontSize: 14,
        background: hover ? 'var(--color-panel-alt)' : 'transparent',
        transition: 'background 120ms',
      }}
    >
      {/* Company name + status */}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {c.companyName ?? '—'}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: statusColor, marginTop: 3 }}>
          <span style={{ width: 5, height: 5, borderRadius: 2.5, background: statusColor, display: 'inline-block' }} />
          {c.inactive ? 'Inactive' : 'Active'}
        </div>
      </div>

      <Cell>{c.contactPersonFirstName ?? '—'}</Cell>
      <Cell>{c.contactPersonLastName ?? '—'}</Cell>
      <Cell mono ellipsis style={{ color: c.contactPersonEmail ? 'var(--color-accent)' : 'var(--color-text-dim)' }}>
        {c.contactPersonEmail || '—'}
      </Cell>
      <Cell mono dim>{c.registrationNumber || '—'}</Cell>
      <Cell mono dim>{c.tel || '—'}</Cell>
      <Cell dim ellipsis>{formatLocation(c.city, c.stateProvince, c.country)}</Cell>

      {/* Actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
        <IconButton title="Edit" onClick={onEdit}>
          <Pencil size={15} />
        </IconButton>
        <IconButton title="Delete" onClick={onDelete} hoverDanger>
          <Trash2 size={15} />
        </IconButton>
      </div>
    </div>
  );
}

function Cell({
  children,
  mono = false,
  dim = false,
  ellipsis = false,
  style,
}: {
  children: React.ReactNode;
  mono?: boolean;
  dim?: boolean;
  ellipsis?: boolean;
  style?: CSSProperties;
}) {
  return (
    <div
      style={{
        fontFamily: mono ? 'var(--font-mono)' : 'var(--font-body)',
        color: dim ? 'var(--color-text-mid)' : 'var(--color-text)',
        fontSize: mono ? 13 : 14,
        whiteSpace: ellipsis ? 'nowrap' : undefined,
        overflow: ellipsis ? 'hidden' : undefined,
        textOverflow: ellipsis ? 'ellipsis' : undefined,
        minWidth: 0,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function IconButton({
  children,
  onClick,
  title,
  hoverDanger,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title: string;
  hoverDanger?: boolean;
}) {
  const [hover, setHover] = useState(false);
  // Always visible: bordered chip with idle background. Hover lifts to brand/danger.
  const idleBg     = 'var(--color-panel)';
  const idleBorder = 'var(--color-border)';
  const idleColor  = hoverDanger ? '#a14a47' : 'var(--color-accent)';
  const hoverBg     = hoverDanger ? 'rgba(185,28,28,0.08)' : 'rgba(11,74,111,0.08)';
  const hoverBorder = hoverDanger ? 'rgba(185,28,28,0.35)' : 'var(--color-accent)';
  const hoverColor  = hoverDanger ? 'var(--color-bad)' : 'var(--color-accent-hover)';

  return (
    <button
      title={title}
      aria-label={title}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: 32,
        height: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 7,
        border: `1px solid ${hover ? hoverBorder : idleBorder}`,
        cursor: 'pointer',
        background: hover ? hoverBg : idleBg,
        color: hover ? hoverColor : idleColor,
        transition: 'background 120ms, color 120ms, border-color 120ms, transform 120ms',
        transform: hover ? 'translateY(-1px)' : 'none',
        boxShadow: hover ? '0 2px 6px rgba(15,23,32,0.08)' : 'none',
      }}
    >
      {children}
    </button>
  );
}

function PrimaryButton({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        background: hover ? 'var(--color-accent-hover)' : 'var(--color-accent)',
        color: '#fff',
        border: 'none',
        padding: '9px 14px',
        borderRadius: 7,
        fontSize: 13,
        fontWeight: 500,
        fontFamily: 'var(--font-body)',
        cursor: 'pointer',
        transition: 'background 120ms',
      }}
    >
      {children}
    </button>
  );
}

function PaginationButton({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 26,
        height: 26,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid var(--color-border)',
        background: 'var(--color-panel)',
        color: disabled ? 'var(--color-text-dim)' : 'var(--color-text-mid)',
        borderRadius: 4,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 12,
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// Filter controls
// ─────────────────────────────────────────────────────────────

// FilterPopover — single button that opens an overlay with Country/State/City.
// Rendered through a Portal so the parent grid's `overflow: hidden` can't clip it.
// Position is recalculated on open + on scroll/resize.
function FilterPopover({
  countryFilter,
  stateFilter,
  cityFilter,
  availableCountries,
  availableStates,
  onChangeCountry,
  onChangeState,
  onChangeCity,
  onClear,
  activeCount,
}: {
  countryFilter: string;
  stateFilter: string;
  cityFilter: string;
  availableCountries: string[];
  availableStates: string[];
  onChangeCountry: (v: string) => void;
  onChangeState: (v: string) => void;
  onChangeCity: (v: string) => void;
  onClear: () => void;
  activeCount: number;
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const POP_WIDTH = 340;

  const updateCoords = useCallback(() => {
    const rect = btnRef.current?.getBoundingClientRect();
    if (!rect) return;
    // Anchor below the button, right-aligned to it, but clamp inside viewport.
    const top = rect.bottom + 8;
    const desiredLeft = rect.left;
    const maxLeft = window.innerWidth - POP_WIDTH - 12;
    const left = Math.max(12, Math.min(desiredLeft, maxLeft));
    setCoords({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (open) updateCoords();
  }, [open, updateCoords]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => updateCoords();
    const onResize = () => updateCoords();
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, updateCoords]);

  const isActive = activeCount > 0;

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 7,
          padding: '7px 12px',
          fontSize: 13,
          fontWeight: 500,
          fontFamily: 'var(--font-body)',
          cursor: 'pointer',
          background: open ? 'var(--color-panel-alt)' : 'var(--color-panel)',
          color: isActive ? 'var(--color-accent)' : 'var(--color-text-mid)',
          border: `1px solid ${isActive ? 'var(--color-accent)' : 'var(--color-border)'}`,
          borderRadius: 7,
          transition: 'all 120ms',
        }}
      >
        <SlidersHorizontal size={14} />
        Filter
        {isActive && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 18,
              height: 18,
              padding: '0 5px',
              borderRadius: 9,
              background: 'var(--color-accent)',
              color: '#fff',
              fontSize: 10.5,
              fontWeight: 700,
              fontFamily: 'var(--font-mono)',
              lineHeight: 1,
            }}
          >
            {activeCount}
          </span>
        )}
      </button>

      {open && coords && createPortal(
        <div
          ref={popRef}
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            width: POP_WIDTH,
            background: 'var(--color-panel)',
            border: '1px solid var(--color-border)',
            borderRadius: 10,
            boxShadow: '0 12px 32px rgba(15,23,32,0.12)',
            zIndex: 100,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px',
              borderBottom: '1px solid var(--color-border)',
            }}
          >
            <div
              style={{
                fontSize: 10.5,
                textTransform: 'uppercase',
                letterSpacing: 1.2,
                color: 'var(--color-text-dim)',
                fontFamily: 'var(--font-mono)',
                fontWeight: 500,
              }}
            >
              Filter by
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close"
              style={{
                width: 22,
                height: 22,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--color-text-dim)',
                borderRadius: 4,
              }}
            >
              <X size={14} />
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <PopoverField label="Country">
              <FilterSelect
                value={countryFilter}
                onChange={onChangeCountry}
                placeholder="All countries"
                options={availableCountries}
              />
            </PopoverField>
            <PopoverField label="State / Province">
              <FilterSelect
                value={stateFilter}
                onChange={onChangeState}
                placeholder={availableStates.length ? 'All states' : '— select country first —'}
                options={availableStates}
                disabled={availableStates.length === 0}
              />
            </PopoverField>
            <PopoverField label="City">
              <FilterCity value={cityFilter} onChange={onChangeCity} />
            </PopoverField>
          </div>

          {/* Footer */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px 16px',
              borderTop: '1px solid var(--color-border)',
              background: 'var(--color-panel-alt)',
            }}
          >
            <button
              onClick={onClear}
              disabled={activeCount === 0}
              style={{
                fontSize: 12,
                color: activeCount === 0 ? 'var(--color-text-dim)' : 'var(--color-accent)',
                background: 'transparent',
                border: 'none',
                cursor: activeCount === 0 ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-body)',
                fontWeight: 500,
                padding: '4px 4px',
              }}
            >
              Clear filters
            </button>
            <button
              onClick={() => setOpen(false)}
              style={{
                fontSize: 12.5,
                fontWeight: 500,
                background: 'var(--color-accent)',
                color: '#fff',
                border: 'none',
                padding: '6px 14px',
                borderRadius: 6,
                cursor: 'pointer',
                fontFamily: 'var(--font-body)',
              }}
            >
              Apply
            </button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

function PopoverField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span
        style={{
          fontSize: 11.5,
          color: 'var(--color-text-mid)',
          fontFamily: 'var(--font-body)',
          fontWeight: 500,
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

function StatusSegmented({
  value,
  onChange,
}: {
  value: StatusFilter;
  onChange: (v: StatusFilter) => void;
}) {
  const opts: { v: StatusFilter; label: string; dot?: string }[] = [
    { v: 'active', label: 'Active', dot: 'var(--color-good)' },
    { v: 'all', label: 'All' },
    { v: 'inactive', label: 'Inactive', dot: 'var(--color-text-dim)' },
  ];
  return (
    <div
      style={{
        display: 'inline-flex',
        padding: 3,
        background: 'var(--color-panel-alt)',
        border: '1px solid var(--color-border)',
        borderRadius: 8,
        gap: 2,
      }}
    >
      {opts.map((o) => {
        const active = value === o.v;
        return (
          <button
            key={o.v}
            onClick={() => onChange(o.v)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '5px 12px',
              border: 'none',
              borderRadius: 6,
              fontSize: 12.5,
              fontWeight: 500,
              fontFamily: 'var(--font-body)',
              cursor: 'pointer',
              background: active ? 'var(--color-panel)' : 'transparent',
              color: active ? 'var(--color-text)' : 'var(--color-text-mid)',
              boxShadow: active ? '0 1px 2px rgba(15,23,32,0.06)' : 'none',
              transition: 'all 120ms',
            }}
          >
            {o.dot && (
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 3,
                  background: o.dot,
                  display: 'inline-block',
                }}
              />
            )}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function FilterSelect({
  value,
  onChange,
  options,
  placeholder,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder: string;
  disabled?: boolean;
}) {
  const hasValue = !!value;
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      style={{
        width: '100%',
        height: 34,
        padding: '0 30px 0 11px',
        fontSize: 13,
        fontFamily: 'var(--font-body)',
        color: hasValue ? 'var(--color-text)' : 'var(--color-text-mid)',
        background:
          `var(--color-panel) url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'><path d='M1 1l4 4 4-4' stroke='%238894a5' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>") no-repeat right 11px center`,
        border: `1px solid ${hasValue ? 'var(--color-accent)' : 'var(--color-border)'}`,
        borderRadius: 7,
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        outline: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        fontWeight: hasValue ? 500 : 400,
        transition: 'border-color 120ms',
      }}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );
}

function FilterCity({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const hasValue = !!value;
  return (
    <input
      placeholder="City contains…"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: '100%',
        height: 34,
        padding: '0 11px',
        fontSize: 13,
        fontFamily: 'var(--font-body)',
        color: 'var(--color-text)',
        background: 'var(--color-panel)',
        border: `1px solid ${hasValue ? 'var(--color-accent)' : 'var(--color-border)'}`,
        borderRadius: 7,
        outline: 'none',
        transition: 'border-color 120ms',
      }}
    />
  );
}
